/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get({
    inboxId,
    status,
    assigneeType,
    page,
    labels,
    teamId,
    conversationType,
    sortBy,
    updatedWithin,
  }) {
    return axios.get(this.url, {
      params: {
        inbox_id: inboxId,
        team_id: teamId,
        status,
        assignee_type: assigneeType,
        page,
        labels,
        conversation_type: conversationType,
        sort_by: sortBy,
        updated_within: updatedWithin,
      },
    });
  }

  filter(payload) {
    return axios.post(`${this.url}/filter`, payload.queryData, {
      params: {
        page: payload.page,
      },
    });
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  toggleStatus({ conversationId, status, snoozedUntil = null }) {
    return axios.post(`${this.url}/${conversationId}/toggle_status`, {
      status,
      snoozed_until: snoozedUntil,
    });
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  toggleWorkforceBot(conversationId) {
    return axios.post(`${this.url}/${conversationId}/toggle_workforce_bot`);
  }

  assignAgent({ conversationId, agentId }) {
    return axios.post(`${this.url}/${conversationId}/assignments`, {
      assignee_id: agentId,
    });
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  // O rastro de execução do PowerAgent nesta conversa (botão de depuração no cabeçalho).
  // Buscado sob demanda, no clique: a maioria das conversas não é de agente, e uma ida ao
  // runtime a cada conversa aberta seria custo permanente para uma tela que quase ninguém
  // abre.
  agentTrace(conversationId) {
    return axios.get(`${this.url}/${conversationId}/agent_trace`);
  }

  agentFollowup(conversationId) {
    return axios.get(`${this.url}/${conversationId}/agent_followup`);
  }

  // `optedOut` é sempre dito, nunca inferido: este método liga E desliga, e o servidor recusa
  // um corpo que não escolha — numa chamada que decide se alguém volta a receber mensagem,
  // adivinhar seria escolher por conta própria.
  setAgentFollowup(conversationId, { optedOut, reason }) {
    return axios.put(`${this.url}/${conversationId}/agent_followup`, {
      opted_out: optedOut,
      reason,
    });
  }

  // O PowerAgent NESTA conversa: se está respondendo ou calado, e por quê. `agent: null`
  // quando a inbox não tem PowerAgent — é o que o cabeçalho usa para escolher o botão.
  agentBot(conversationId) {
    return axios.get(`${this.url}/${conversationId}/bot`);
  }

  // `active` é sempre dito, como no follow-up: este método pausa E retoma, e o servidor
  // recusa corpo sem escolha — adivinhar aqui decidiria se o bot volta a falar com o cliente.
  setAgentBot(conversationId, { active }) {
    return axios.put(`${this.url}/${conversationId}/bot`, { active });
  }

  // Por que o bot está calado (ou voltou): o rastro de transições, mais recente primeiro.
  agentBotEvents(conversationId) {
    return axios.get(`${this.url}/${conversationId}/bot_events`);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  meta({ inboxId, status, assigneeType, labels, teamId, conversationType }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
      },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  getInboxAssistant(conversationId) {
    return axios.get(`${this.url}/${conversationId}/inbox_assistant`);
  }

  delete(conversationId) {
    return axios.delete(`${this.url}/${conversationId}`);
  }
}

export default new ConversationApi();
